import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  AttachmentList,
  Divider,
  FileSize,
  FileType,
  Modal,
  RichText,
  Spacer,
  Spinner,
} from '@porsche-code/kado-ui';
import { PButton, PText } from '@porsche-design-system/components-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { ClientError } from 'graphql-request';
import { ParseKeys } from 'i18next';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UAParser } from 'ua-parser-js';

import {
  ApplicationTags,
  Comments,
  DateTimeOutput,
  SupportRequestStatusTag,
} from '@/components';
import { NAMESPACES } from '@/config/i18n';
import {
  DownloadReference,
  DownloadType,
  SupportRequestQuery,
  SupportRequestsActivityQuery,
  SupportRequestsQuery,
  useCreateDownloadUrlMutation,
  useSupportRequestQuery,
  useSupportRequestsActivityQuery,
  useSupportRequestsQuery,
  useUpdateSupportRequestMutation,
} from '@/graphql';

import { SupportRequestCommentForm } from './-components';

export const Route = createFileRoute('/support/requests/$requestId')({
  component: SupportRequest,
  beforeLoad: () => {
    return {
      breadcrumb: () => <SupportRequestBreadcrumb />,
    };
  },
});

const SupportRequestBreadcrumb = () => {
  const { requestId } = useParams({
    from: '/support/requests/$requestId',
  });
  const { data } = useSupportRequestQuery(
    {
      id: requestId,
    },
    {
      select: ({ supportRequest }) => supportRequest,
    },
  );

  return <>{data?.title}</>;
};

const ContentWrapper = styled('div', {
  base: {
    maxWidth: 'content',
  },
});

const Grid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
  },
  variants: {
    gap: {
      small: {
        gap: 'sm',
      },
      medium: {
        gap: 'md',
      },
    },
  },
  defaultVariants: {
    gap: 'medium',
  },
});

function SupportRequest() {
  const { t } = useTranslation(NAMESPACES);

  const queryClient = useQueryClient();

  const { requestId } = useParams({
    from: '/support/requests/$requestId',
  });

  const { data: supportRequest, isPending } = useSupportRequestQuery(
    {
      id: requestId,
    },
    {
      select: (data) => data.supportRequest,
    },
  );

  useEffect(() => {
    const updateLocalStorage = async () => {
      if (supportRequest) {
        await useUpdateSupportRequestMutation.fetcher({
          input: {
            id: supportRequest.id,
            lastSeenAt: new Date().toISOString(),
          },
        })();

        queryClient.setQueryData<SupportRequestsQuery>(
          useSupportRequestsQuery.getKey(),
          (cache) =>
            cache
              ? {
                  ...cache,
                  supportRequests: cache.supportRequests.map((sr) =>
                    sr.id === supportRequest.id
                      ? { ...sr, hasNewActivity: false }
                      : sr,
                  ),
                }
              : undefined,
        );
        queryClient.setQueryData<SupportRequestsActivityQuery>(
          useSupportRequestsActivityQuery.getKey(),
          (cache) =>
            cache
              ? {
                  ...cache,
                  supportRequests: cache.supportRequests.map((sr) =>
                    sr.id === supportRequest.id
                      ? { ...sr, hasNewActivity: false }
                      : sr,
                  ),
                }
              : undefined,
        );
      }
    };
    updateLocalStorage();
  }, [supportRequest, queryClient]);

  return (
    <ContentWrapper>
      {isPending ? (
        <Spinner size="xl" />
      ) : !supportRequest ? (
        <PText>{t('common:noData')}</PText>
      ) : (
        <>
          <Grid>
            <div>
              <strong>{t('supportRequest.ticketId')}</strong>
            </div>
            <div>{supportRequest.id}</div>
            <div>
              <strong>{t('common:status')}</strong>
            </div>
            <div>
              <SupportRequestStatusTag status={supportRequest.status} />
            </div>
            <div>
              <strong>{t('common:date')}</strong>
            </div>
            <div>
              <DateTimeOutput date={supportRequest.createdAt} />
            </div>
            <div>
              <strong>{t('supportRequest.application')}</strong>
            </div>
            <div>
              <ApplicationTags apps={[supportRequest.application]} />
            </div>
            <div>
              <strong>{t('common:organization')}</strong>
            </div>
            <div>
              {supportRequest.organization.name}
              <PText size="xs">
                ({t('common:id')}: {supportRequest.organization.id})
              </PText>
            </div>
            <div>
              <strong>{t('common:person')}</strong>
            </div>
            <div>
              {supportRequest.author.name}
              <PText size="xs">
                ({t('common:ppnuid')}: {supportRequest.author.ppnuid})
              </PText>
            </div>
            {supportRequest.referencePerson && (
              <>
                <div>
                  <strong>{t('supportRequest.referencePerson')}</strong>
                </div>
                <div>
                  {supportRequest.referencePerson.name}
                  <PText size="xs">
                    ({t('common:ppnuid')}:{' '}
                    {supportRequest.referencePerson.ppnuid})
                  </PText>
                </div>
              </>
            )}
            <div>
              <strong>{t('supportRequest.title')}</strong>
            </div>
            <div>{supportRequest.title}</div>
            <div>
              <strong>{t('supportRequest.description')}</strong>
            </div>
            <div>
              <RichText value={JSON.parse(supportRequest.description ?? '')} />
            </div>

            <TechnicalDetails details={supportRequest.details} />

            <div>
              <strong>{t('common:attachments')}</strong>
            </div>
            <div>
              <AttachmentList>
                {supportRequest.attachments?.map((attachment) => (
                  <AttachmentItem key={attachment.id} attachment={attachment} />
                ))}
              </AttachmentList>
            </div>
          </Grid>

          <Divider />

          {supportRequest.comments.length > 0 && (
            <Comments comments={supportRequest.comments} />
          )}

          <Spacer mb="xl" />

          <SupportRequestCommentForm
            id={supportRequest.id}
            title={supportRequest.title}
            status={supportRequest.status}
          />
        </>
      )}
    </ContentWrapper>
  );
}

const TechnicalDetails = ({
  details,
}: {
  details?: NonNullable<SupportRequestQuery['supportRequest']>['details'];
}) => {
  const { t } = useTranslation();

  if (!details) return null;

  // oxlint-disable-next-line react/capitalized-calls
  const { browser, device, os } = UAParser(details.userAgent);

  return (
    <>
      <div>
        <strong>{t('supportRequest.technicalDetails')}</strong>
      </div>
      <div>
        <Grid gap="small">
          <div>{t('supportRequest.browser')}</div>
          <div>
            {browser.name} ({browser.version})
          </div>

          <div>{t('supportRequest.operatingSystem')}</div>
          <div>
            {os.name} ({os.version})
          </div>

          <div>{t('supportRequest.device')}</div>
          <div>
            {device.vendor} {device.model} {device.type}
          </div>

          <div>{t('supportRequest.screenSize')}</div>
          <div>
            {details.screenSize.width} x {details.screenSize.height}
          </div>

          <div>{t('supportRequest.browserWindowSize')}</div>
          <div>
            {details.browserWindowSize.width} x{' '}
            {details.browserWindowSize.height}
          </div>
        </Grid>
      </div>
    </>
  );
};

const AttachmentItem = ({
  attachment,
}: {
  attachment: {
    id: string;
    name: string;
    type: string;
    size: number;
    createdAt?: string;
  };
}) => {
  const { t } = useTranslation(NAMESPACES);

  const { mutateAsync: createDownloadUrl } = useCreateDownloadUrlMutation();

  const createAttachmentDownloadUrl = async (id: string) => {
    const { createDownloadUrl: downloadUrl } = await createDownloadUrl({
      id,
      type: DownloadType.Attachment,
      reference: DownloadReference.Dashboard,
    });

    return downloadUrl;
  };

  const downloadAttachmentMutation = useMutation<void, ClientError, string>({
    mutationFn: async (attachmentId) => {
      const downloadUrl = await createAttachmentDownloadUrl(attachmentId);
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      saveAs(blob, attachment.name);
    },
  });

  return (
    <>
      <AttachmentList.Item
        key={attachment.id}
        fileType={attachment.type}
        isLoading={downloadAttachmentMutation.isPending}
        onClick={() => {
          downloadAttachmentMutation.reset();
          downloadAttachmentMutation.mutate(attachment.id);
        }}
        canPreview={attachment.type.startsWith('image/')}
        previewHeading={attachment.name}
        onPreview={async () => {
          try {
            const url = await createAttachmentDownloadUrl(attachment.id);
            return { url };
          } catch (error: unknown) {
            if (error instanceof ClientError) {
              const message =
                error.response?.errors?.[0]?.message ?? error.message;
              throw new Error(t(message as ParseKeys), { cause: error });
            }

            throw new Error(t('common:upload.securityInspection.fallback'), {
              cause: error,
            });
          }
        }}
        i18n={{
          cancel: t('common:action.cancel'),
          download: t('common:download'),
        }}
      >
        {attachment.createdAt && (
          <AttachmentList.Item.Meta
            label={t('common:upload.uploadDateShort')}
            value={
              dayjs(attachment.createdAt).isValid()
                ? t('common:upload.uploadDateDate', {
                    date: new Date(attachment.createdAt),
                  })
                : null
            }
          />
        )}
        <AttachmentList.Item.Meta
          label={t('common:upload.fileSize')}
          value={<FileSize value={attachment.size} />}
        />
        <AttachmentList.Item.Meta
          label={t('common:upload.fileType')}
          value={<FileType value={attachment.type} hideIcon />}
        />
      </AttachmentList.Item>

      <Modal
        open={downloadAttachmentMutation.isError}
        onDismiss={() => downloadAttachmentMutation.reset()}
      >
        <Modal.Header>
          {t('common:upload.securityInspection.headline')}
        </Modal.Header>
        <PText>
          {downloadAttachmentMutation.error instanceof ClientError
            ? t(
                (downloadAttachmentMutation.error.response?.errors?.[0]
                  ?.message ??
                  downloadAttachmentMutation.error.message) as ParseKeys,
              )
            : t('common:upload.securityInspection.fallback')}
        </PText>
        <Modal.Footer sticky>
          <PButton
            type="button"
            variant="secondary"
            icon="close"
            onClick={() => downloadAttachmentMutation.reset()}
          >
            {t('common:action.cancel')}
          </PButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
